/**
 * Per-user column preference for a desk `DataTable`, saved like Play
 * Archive's: localStorage paints instantly (offline cache), the saved layout
 * `PUT /users/v1/layouts/user/table.<surface>` wins when it lands, and every
 * change is written to both.
 *
 * `surface` is a short kebab-case name, e.g. `team-users` or `roster`; the
 * server stores it as screen id `table.team-users` (freeform `table.*` slot).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useOptionalApiClient } from '../app/providers';

import { reconcilePrefs, type ColumnPrefs, type FitColumn } from './table-layout';

export const TABLE_LAYOUT_PREFIX = 'table.';
const STORAGE_PREFIX = 'gridiron.table-columns.v1.';

/** Wire shape stored under the user's `table.<surface>` layout slot. */
export interface TableColumnsLayout {
  kind: 'table-columns';
  version: 1;
  columns: { id: string; visible: boolean }[];
  density?: ColumnPrefs['density'];
}

export function tableScreenId(surface: string): string {
  return `${TABLE_LAYOUT_PREFIX}${surface}`;
}

export function toTableLayout(prefs: ColumnPrefs): TableColumnsLayout {
  return {
    kind: 'table-columns',
    version: 1,
    columns: prefs.columns.map((entry) => ({ id: entry.id, visible: entry.visible })),
    density: prefs.density,
  };
}

export function fromTableLayout(layout: unknown): Partial<ColumnPrefs> | null {
  if (typeof layout !== 'object' || layout === null) return null;
  const record = layout as Partial<TableColumnsLayout>;
  if (record.kind !== 'table-columns' || !Array.isArray(record.columns)) return null;
  return {
    columns: record.columns.filter(
      (entry): entry is { id: string; visible: boolean } =>
        typeof entry === 'object' && entry !== null && typeof entry.id === 'string'
    ),
    density: record.density === 'comfortable' ? 'comfortable' : 'compact',
  };
}

function readCached(surface: string): Partial<ColumnPrefs> | null {
  try {
    const raw = globalThis.localStorage?.getItem(`${STORAGE_PREFIX}${surface}`);
    return raw === null || raw === undefined || raw === ''
      ? null
      : fromTableLayout(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeCached(surface: string, prefs: ColumnPrefs): void {
  try {
    globalThis.localStorage?.setItem(
      `${STORAGE_PREFIX}${surface}`,
      JSON.stringify(toTableLayout(prefs))
    );
  } catch {
    // Storage disabled (private mode) must not break the table.
  }
}

export interface TableColumnPrefsState {
  prefs: ColumnPrefs;
  setPrefs: (next: ColumnPrefs) => void;
  /** Server save failed; the choice is kept on this device only. */
  isSavedLocallyOnly: boolean;
}

/**
 * `surface === undefined` keeps the preference in memory only (no picker
 * persistence) — for tables that do not offer a Columns picker.
 */
export function useTableColumnPrefs(
  surface: string | undefined,
  columns: readonly FitColumn[],
  label = 'Table columns'
): TableColumnPrefsState {
  const apiClient = useOptionalApiClient();
  const [stored, setStored] = useState<Partial<ColumnPrefs> | null>(() =>
    surface === undefined ? null : readCached(surface)
  );
  const [isSavedLocallyOnly, setIsSavedLocallyOnly] = useState(false);
  const touchedRef = useRef(false);

  useEffect(() => {
    if (surface === undefined || apiClient === null) return undefined;
    const controller = new AbortController();
    const screenId = tableScreenId(surface);
    Promise.resolve()
      .then(() => apiClient.listLayouts(controller.signal))
      .then((page) => {
        if (controller.signal.aborted || touchedRef.current) return;
        const record = page?.items?.find(
          (item) => item.scope === 'user' && item.screen_id === screenId
        );
        const parsed = record === undefined ? null : fromTableLayout(record.layout);
        if (parsed !== null) setStored(parsed);
      })
      .catch(() => {
        // Offline / users service down: the cached preference stands.
      });
    return () => controller.abort();
  }, [apiClient, surface]);

  const prefs = useMemo(() => reconcilePrefs(stored, columns), [stored, columns]);

  const setPrefs = useCallback(
    (next: ColumnPrefs): void => {
      touchedRef.current = true;
      setStored(next);
      if (surface === undefined) return;
      writeCached(surface, next);
      if (apiClient === null) return;
      Promise.resolve()
        .then(() =>
          apiClient.saveLayout('user', tableScreenId(surface), {
            name: label,
            layout: toTableLayout(next) as unknown as Record<string, unknown>,
          })
        )
        .then(() => setIsSavedLocallyOnly(false))
        .catch(() => setIsSavedLocallyOnly(true));
    },
    [apiClient, surface, label]
  );

  return { prefs, setPrefs, isSavedLocallyOnly };
}
